import { LegalLayout, LegalList, LegalSection } from '../../ui/LegalLayout.js'

/**
 * What the product does with data, written from what the code actually does.
 *
 * Every claim here is checkable against this repository, and that is the point
 * of it rather than a nicety: a privacy notice is the one document a company
 * publishes that is simultaneously a promise and a description, and the
 * description going stale is how the promise becomes a lie. Three things in
 * particular were verified when this was written and must be re-verified when
 * they change — there are no analytics or advertising scripts anywhere in the
 * front end, there is no open or click tracking in any sent message, and the
 * customer application authenticates with a bearer token rather than a cookie
 * of its own.
 *
 * **Two different people appear in this document and they have different
 * rights.** An account holder signs up and agrees to this. A contact never
 * did: their address was uploaded by an account holder. We are the processor
 * for the second group and the controller only for the first, so the notice
 * says, for each kind of data, which one it is about.
 *
 * Google's OAuth review requires this to be reachable without signing in,
 * which is why the route is public (ADR 0004 put identity behind Clerk; the
 * Gmail scope that sends mail is what the review is for).
 */
/**
 * Where a data request lands.
 *
 * A working mailbox, not a role address invented for the page: a privacy notice
 * naming an address that bounces is worse than one naming a person, because the
 * request fails silently and the sender believes it was received. Change it to
 * a role address once that role address exists and is monitored.
 */
const CONTACT = 'akhan@carbonweb.co'

export function PrivacyPage() {
  return (
    <LegalLayout title="Privacy notice" updated="29 October 2026">
      <LegalSection heading="Who this is about">
        <p>
          Carbon Mail is an email platform operated by CarbonWeb. Two different kinds of people
          appear in it, and this notice treats them differently because the law does.
        </p>
        <LegalList>
          <li>
            <strong>Account holders</strong> — people who sign up and use the product. We decide how
            their account data is handled, so we are the controller of it.
          </li>
          <li>
            <strong>Contacts</strong> — people whose email addresses an account holder has uploaded.
            The account holder decides who is on their list and what is sent. We only hold and send
            on their instruction, so we are a processor and they are the controller.
          </li>
        </LegalList>
        <p>
          If you received an email through Carbon Mail and want your data removed, the fastest route
          is the unsubscribe link in that email, which takes effect immediately and permanently. You
          may also contact us and we will pass the request to the sender.
        </p>
      </LegalSection>

      <LegalSection heading="What we hold about account holders">
        <LegalList>
          <li>
            Your name, email address and sign-in credentials. Credentials are handled entirely by
            our identity provider, Clerk — no password ever reaches our servers or our database.
          </li>
          <li>Which organisation you belong to and what you are allowed to do in it.</li>
          <li>
            Credentials for the email provider you connect, encrypted at rest with a key held
            outside the database.
          </li>
          <li>A record of significant actions taken in the account, so changes are accountable.</li>
        </LegalList>
      </LegalSection>

      <LegalSection heading="What we hold about contacts">
        <LegalList>
          <li>Email address, and any name or custom fields the account holder chose to upload.</li>
          <li>
            Subscription status, and an append-only consent record — where the address came from and
            when — which exists so a sender can evidence permission.
          </li>
          <li>
            A log of what was sent and what the receiving mail server said back: accepted, rejected,
            bounced, or a complaint.
          </li>
          <li>
            A suppression entry if they unsubscribed, hard-bounced, or complained, kept with the
            reason.
          </li>
        </LegalList>
        <p>
          <strong>We do not track opens or clicks.</strong> Carbon Mail puts no tracking pixel and
          no rewritten link into any message it sends. What a recipient does after a message arrives
          is not recorded, because it is not collected.
        </p>
      </LegalSection>

      <LegalSection heading="What we do not do">
        <LegalList>
          <li>We never sell personal data, and we never share it for advertising.</li>
          <li>
            We do not use contact data to train models, and we do not use one account holder's data
            to improve anything for another.
          </li>
          <li>
            The application runs no analytics, advertising or session-replay scripts. The only
            cookies set are those our identity provider needs to keep you signed in.
          </li>
        </LegalList>
      </LegalSection>

      <LegalSection heading="Who else processes it">
        <p>These are the only third parties that data reaches, and why:</p>
        <LegalList>
          <li>
            <strong>Clerk</strong> — identity. Account holders' sign-in details and credentials.
          </li>
          <li>
            <strong>Your chosen email provider</strong> — delivery. Whichever of Gmail, Amazon SES,
            Postmark, Resend, Brevo or your own SMTP server you connect receives the messages and
            the addresses they go to. This is the account holder's choice, not ours.
          </li>
          <li>
            <strong>Anthropic</strong> — only when the AI email designer is used, and only the brief
            and message content given to it. Contact lists are never sent.
          </li>
          <li>
            <strong>Hostinger</strong> — hosting. Our servers and database are in the United States.
          </li>
        </LegalList>
      </LegalSection>

      <LegalSection heading="How long we keep it">
        <LegalList>
          <li>Account data: for as long as the account exists, then deleted on request.</li>
          <li>
            Contacts: until the account holder deletes them or closes the account. Deleting a
            contact removes their record.
          </li>
          <li>
            <strong>Suppression entries outlive the contact, deliberately.</strong> If someone
            unsubscribed or complained, deleting that record would let a later import start sending
            to them again. We keep the address in suppressed form so that cannot happen.
          </li>
          <li>
            Send and consent records are kept as evidence that a message was sent and that
            permission existed.
          </li>
        </LegalList>
      </LegalSection>

      <LegalSection heading="Security">
        <LegalList>
          <li>Everything travels over TLS. Provider credentials are encrypted at rest.</li>
          <li>
            Tenant separation is enforced by the database itself through row-level security, not
            only by application code, so a mistake in a query cannot return another organisation's
            rows.
          </li>
          <li>The database is backed up daily.</li>
        </LegalList>
      </LegalSection>

      <LegalSection heading="Your rights">
        <p>
          Depending on where you live, you may have the right to access, correct, export or delete
          your personal data, to object to processing, and to complain to a data protection
          authority.
        </p>
        <p>
          <strong>Account holders</strong> can exercise these by contacting us.{' '}
          <strong>Contacts</strong> should contact the sender whose email they received, because
          that sender decides what happens to their list; if you contact us instead we will pass it
          on and tell you who it went to.
        </p>
      </LegalSection>

      <LegalSection heading="Changes, and how to reach us">
        <p>
          If this notice changes in a way that affects what happens to your data, we will say so on
          this page and change the date at the top.
        </p>
        <p>
          Questions, or a request about your data: <strong>{CONTACT}</strong>.
        </p>
      </LegalSection>
    </LegalLayout>
  )
}
